import type { ModuleId, ShellPersona } from '../app-shell/moduleRegistry';
import type { ShellState } from './shellSlice';
import { SHELL_STATE_VERSION } from './shellSlice';

const STORAGE_PREFIX = 'ystnj.shell.v2';

const isModuleId = (value: unknown): value is ModuleId =>
  typeof value === 'string'
  && ['assistant', 'admissions', 'financial-aid', 'payroll', 'parent-portal', 'students-families', 'faculty', 'settings', 'tuition'].includes(value);

const isShellPersona = (value: unknown): value is ShellPersona =>
  typeof value === 'string'
  && ['parent', 'staff', 'administrator', 'super-administrator', 'guest'].includes(value);

const normalizeAssistantWidthMode = (value: unknown): ShellState['assistantWidthMode'] | undefined => {
  if (value === 'default') {
    return 'compact';
  }
  if (value === 'wide') {
    return 'half';
  }
  if (value === 'compact' || value === 'expanded' || value === 'half' || value === 'full') {
    return value;
  }
  return undefined;
};

const normalizeState = (input: Partial<ShellState>): Partial<ShellState> => ({
  lastVisitedPath: typeof input.lastVisitedPath === 'string' ? input.lastVisitedPath : undefined,
  defaultModule: isModuleId(input.defaultModule) ? input.defaultModule : null,
  navOpen: typeof input.navOpen === 'boolean' ? input.navOpen : undefined,
  assistantOpen: typeof input.assistantOpen === 'boolean' ? input.assistantOpen : undefined,
  assistantWidthMode: normalizeAssistantWidthMode(input.assistantWidthMode),
  showEnvBanner: typeof input.showEnvBanner === 'boolean' ? input.showEnvBanner : undefined,
  personaOverride: isShellPersona(input.personaOverride) ? input.personaOverride : null,
});

export const resolveShellStorageKey = (userSub?: string | null): string =>
  userSub ? `${STORAGE_PREFIX}.${userSub}` : `${STORAGE_PREFIX}.anon`;

export const readShellState = (key: string): Partial<ShellState> | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ShellState> & { version?: number };
    if (parsed.version !== SHELL_STATE_VERSION) {
      return null;
    }
    return normalizeState(parsed);
  } catch {
    return null;
  }
};

export const writeShellState = (key: string, state: ShellState): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const payload = {
      version: SHELL_STATE_VERSION,
      lastVisitedPath: state.lastVisitedPath ?? null,
      defaultModule: state.defaultModule ?? null,
      navOpen: state.navOpen,
      assistantOpen: state.assistantOpen,
      assistantWidthMode: state.assistantWidthMode,
      showEnvBanner: state.showEnvBanner,
      personaOverride: state.personaOverride ?? null,
      updatedAt: state.updatedAt,
    };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // Swallow storage errors (quota, privacy mode) to keep UI responsive.
  }
};

export const clearShellStorage = (userSub?: string | null): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const key = resolveShellStorageKey(userSub ?? null);
    window.localStorage.removeItem(key);
    window.localStorage.removeItem(`${STORAGE_PREFIX}.anon`);
    window.localStorage.removeItem('ystnj_persona');
    window.localStorage.removeItem('triedge_persona');
  } catch {
    // ignore
  }
};
